<template>
  <header class="app-header" :class="{ 'telegram-miniapp': isTelegramMiniApp }">
    <div class="app-header-container">
      <!-- Logo with App Name -->
      <a :href="logoUrl" class="logo-link">
        <div class="logo-container">
          <LogoIcon />
        </div>
        <div class="logo-text">
          <div class="app-name">Trac<span class="app-name-highlight">k</span>tor</div>
          <div class="app-slogan">{{ s__('Header|Full steam ahead!') }}</div>
        </div>
      </a>

      <!-- User Info -->
      <div v-if="user" class="user-info">
        <a :href="profileUrlWithParams" class="user-link">
          <div class="user-details">
            <div class="user-name">{{ user.name }}</div>
            <div class="user-username">@{{ user.username }}</div>
          </div>
          <div class="user-avatar">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="user-icon"
              aria-hidden="true"
            >
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
        </a>
        <button
          v-if="showLogout"
          @click="handleLogout"
          class="logout-button"
          :title="s__('Header|Logout')"
          :disabled="loggingOut"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
        </button>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { s__ } from '@/locale'
import LogoIcon from './icons/LogoIcon.vue'

const props = defineProps({
  user: {
    type: Object,
    default: null,
  },
  dashboardUrl: {
    type: String,
    default: '/dashboard',
  },
  profileUrl: {
    type: String,
    default: '/profile',
  },
  chatId: {
    type: String,
    default: null,
  },
  fromId: {
    type: String,
    default: null,
  },
})

// Build dashboard URL with Telegram params
const logoUrl = computed(() => {
  let url = props.dashboardUrl
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += (url.includes('?') ? '&' : '?') + params.join('&')
  return url
})

// Build profile URL with Telegram params
const profileUrlWithParams = computed(() => {
  let url = props.profileUrl
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += (url.includes('?') ? '&' : '?') + params.join('&')
  return url
})

const loggingOut = ref(false)

// Check if running in Telegram mini-app
const isTelegramMiniApp = computed(() => {
  return !!(window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData)
})

// Show logout button only if NOT in Telegram mini-app
const showLogout = computed(() => {
  return !isTelegramMiniApp.value
})

// Hide Telegram's native header controls when in mini-app
onMounted(() => {
  if (isTelegramMiniApp.value && window.Telegram?.WebApp) {
    // Hide the header
    window.Telegram.WebApp.setHeaderColor('#f5f5f5')

    // Expand the mini-app to full height
    window.Telegram.WebApp.expand()
  }
})

async function handleLogout() {
  if (loggingOut.value) return

  try {
    loggingOut.value = true

    const response = await fetch('/logout', {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
      },
      redirect: 'follow',
    })

    // If the response is a redirect, the browser will follow it automatically
    // If it's already at the destination, we'll get a 200
    if (response.ok || response.redirected) {
      window.location.href = response.url || '/'
    } else {
      console.error('Logout failed:', response.statusText)
      loggingOut.value = false
    }
  } catch (error) {
    console.error('Logout error:', error)
    loggingOut.value = false
  }
}
</script>

<style scoped>
.app-header {
  background-color: var(--header-bg, #f5f5f5);
  border-bottom: 1px solid var(--header-border, #e0e0e0);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Non-sticky header for Telegram mini-app */
.app-header.telegram-miniapp {
  position: relative;
}

.app-header-container {
  max-width: 640px; /* Mobile: comfortable width for small screens */
  width: 100%;
  margin: 0 auto;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Tablet: wider container for better content visibility */
@media (min-width: 768px) {
  .app-header-container {
    max-width: 768px;
  }
}

/* Desktop: optimal width for task management and content density */
@media (min-width: 1024px) {
  .app-header-container {
    max-width: 820px;
  }
}

/* Large Desktop: take advantage of larger screens without being too wide */
@media (min-width: 1440px) {
  .app-header-container {
    max-width: 900px;
  }
}

.logo-link {
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-container {
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, #2aabee 0%, #229ed9 100%);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(42, 171, 238, 0.2);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  flex-shrink: 0;
}

.logo-link:hover .logo-container {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(42, 171, 238, 0.3);
}

.logo-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.app-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary, #1a1a1a);
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.app-name-highlight {
  background: linear-gradient(135deg, #2aabee, #229ed9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.app-slogan {
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--text-secondary, #666);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.user-link:hover {
  opacity: 0.8;
}

.user-avatar {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #2aabee 0%, #229ed9 100%);
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: white;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  text-align: right;
}

.user-name {
  font-weight: 600;
  font-size: 0.9375rem;
  color: var(--text-primary, #1a1a1a);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-username {
  font-size: 0.8125rem;
  color: var(--text-secondary, #666);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.logout-button {
  padding: 0.5rem;
  background: transparent;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  color: var(--text-secondary, #666);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.logout-button:hover {
  background-color: var(--hover-bg, #f0f0f0);
  color: var(--danger-color, #dc3545);
}

.logout-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .app-header-container {
    padding: 0.5rem 0.75rem;
  }

  .logo-link,
  .user-link {
    padding: 0.2rem;
    border-radius: 0.75rem;
    transition:
      background-color 0.15s ease,
      transform 0.15s ease;
  }

  .logo-link:active,
  .user-link:active {
    background-color: #eaf6fc;
    transform: scale(0.98);
  }

  .logo-container {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.75rem;
    box-shadow:
      0 0 0 2px #eaf6fc,
      0 3px 10px rgba(42, 171, 238, 0.22);
  }

  .logo-text {
    display: none;
  }

  .user-avatar {
    width: 2.5rem;
    height: 2.5rem;
    box-shadow: 0 0 0 2px #eaf6fc;
  }

  .user-icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .user-details {
    max-width: 120px;
  }

  .user-name {
    font-size: 0.875rem;
  }

  .user-username {
    font-size: 0.75rem;
  }
}

/* Dark theme support removed - we use consistent colors regardless of Telegram theme */
</style>
